import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

// Context Providers
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { SocketProvider } from './contexts/SocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CallProvider } from './contexts/CallContext';
import GlobalCallOverlay from './components/chat/VideoCallOverlay';
import FloatingAIAssistant from './components/chat/FloatingAIAssistant';
import AppUpdateManager from './components/update/AppUpdateManager';
import MobileAppInstallBanner from './components/update/MobileAppInstallBanner';
import { WifiOff } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import toast from 'react-hot-toast';
import { initCsrfToken } from './utils/api';

// Components
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Pages - Lazy Loaded for Performance
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const Verify2FA = lazy(() => import('./pages/auth/Verify2FA'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const Mentorship = lazy(() => import('./pages/Mentorship'));
const Jobs = lazy(() => import('./pages/Jobs'));
const Events = lazy(() => import('./pages/Events'));
const Forum = lazy(() => import('./pages/Forum'));

const Chat = lazy(() => import('./pages/Chat'));
const Admin = lazy(() => import('./pages/Admin'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Network = lazy(() => import('./pages/Network'));
const Settings = lazy(() => import('./pages/Settings'));
const Notifications = lazy(() => import('./pages/Notifications'));
const DevPulse = lazy(() => import('./pages/DevPulse'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Feedback = lazy(() => import('./pages/Feedback'));
const HelpCentrePage = lazy(() => import('./pages/HelpCentrePage'));
const ProjectShowcase = lazy(() => import('./pages/ProjectShowcase'));
const ResumeAnalyzer = lazy(() => import('./pages/ResumeAnalyzer'));
const BusinessDirectory = lazy(() => import('./pages/BusinessDirectory'));
const ActivityHub = lazy(() => import('./pages/ActivityHub'));
const Calendar = lazy(() => import('./pages/Calendar'));


const TechHub = lazy(() => import('./pages/tech-hub/TechHub'));
const CareerBoard = lazy(() => import('./pages/opportunities/CareerBoard'));
const ProjectCollaboration = lazy(() => import('./pages/projects/ProjectCollaboration'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));

// Legal and Support Pages - Lazy Loaded
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/legal/TermsConditions'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));
const HelpCentre = lazy(() => import('./pages/support/HelpCentre'));
const ContactUs = lazy(() => import('./pages/support/ContactUs'));
const FAQ = lazy(() => import('./pages/support/FAQ'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't retry auth errors — they won't resolve without a fresh login
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2; // retry other errors up to 2 times
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000), // exponential backoff
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 60 * 1000, // 2 hours
      cacheTime: 3 * 60 * 60 * 1000, // 3 hours
    },
    mutations: {
      retry: false, // never auto-retry mutations
    },
  },
});

const OfflineBanner = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[10000] bg-red-500 text-white text-center py-2 flex items-center justify-center gap-2 shadow-lg">
      <WifiOff className="w-4 h-4 animate-pulse" />
      <span className="text-sm font-medium">You are currently offline. Some features may not work.</span>
    </div>
  );
};

const MobileDeepLinkHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let listener = null;
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appUrlOpen', async (event) => {
        try {
          if (!event?.url) return;
          try {
            await Browser.close();
          } catch (e) {}

          const rawUrl = event.url;
          if (rawUrl.includes('/oauth/google-calendar')) {
            const url = new URL(rawUrl);
            const status = url.searchParams.get('status');
            const error = url.searchParams.get('error');

            if (error) {
              toast.error(decodeURIComponent(error));
            } else if (status === 'success') {
              toast.success('Google Calendar connected successfully!', { icon: '📅' });
              navigate('/calendar');
            }
          }
        } catch (err) {
          console.error('[MobileDeepLinkHandler] Error:', err);
        }
      }).then(l => { listener = l; });
    }

    return () => {
      if (listener) listener.remove();
    };
  }, [navigate]);

  return null;
};

// Controls first-time device visit vs returning user routing
const RootRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const forceLanding = searchParams.get('landing') === 'true';

  if (forceLanding) {
    return <Home />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // If already logged in, go straight to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check if device has visited before
  const hasVisited = (() => {
    try {
      return localStorage.getItem('alumnex_device_visited') === 'true';
    } catch (e) {
      return false;
    }
  })();

  // If new device, show the landing page
  if (!hasVisited) {
    return <Home />;
  }

  // Returning unauthenticated user goes to sign in page
  return <Navigate to="/login" replace />;
};

// Handles cold-start session recovery in app and browser
const AppStartupHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    // Proactively initialize CSRF token on startup
    initCsrfToken().catch(() => {});

    const isColdStartup = !sessionStorage.getItem('alumnex_session_active');
    if (isColdStartup) {
      sessionStorage.setItem('alumnex_session_active', 'true');
      const hasVisited = (() => {
        try {
          return localStorage.getItem('alumnex_device_visited') === 'true';
        } catch (e) {
          return false;
        }
      })();

      if (!isLoading) {
        if (isAuthenticated && (location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/')) {
          navigate('/dashboard', { replace: true });
        } else if (!isAuthenticated && hasVisited && location.pathname === '/register') {
          // If app was reopened and user is not logged in, take them to sign in page instead of signup
          navigate('/login', { replace: true });
        }
      }
    }
  }, [isLoading, isAuthenticated, location.pathname, navigate]);

  return null;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ProfileProvider>
            <SocketProvider>
              <NotificationProvider>
              <Router>
                <MobileDeepLinkHandler />
                <AppStartupHandler />
                <CallProvider>
                  <div className="App min-h-screen relative">
                    <OfflineBanner />
                    <GlobalCallOverlay />
                    <FloatingAIAssistant />
                    <AppUpdateManager />
                    <MobileAppInstallBanner />
                    <div className="animated-bg"></div>

                  <ErrorBoundary>
                    <Suspense fallback={
                      <div className="flex items-center justify-center min-h-screen">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                      </div>
                    }>
                      <Routes>
                    {/* Public Routes */}
                  <Route path="/" element={<RootRoute />} />
                  <Route path="/landing" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/verify-2fa" element={<Verify2FA />} />
                  
                  {/* Legal and Support Routes */}
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsConditions />} />
                  <Route path="/cookies" element={<CookiePolicy />} />
                  <Route path="/help" element={<HelpCentre />} />
                  <Route path="/contact" element={<ContactUs />} />
                  <Route path="/faq" element={<FAQ />} />
                  
                  {/* Protected Routes */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <Layout>
                        <Profile />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/users/:id" element={
                    <ProtectedRoute>
                      <Layout>
                        <UserProfile />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/feedback" element={
                    <ProtectedRoute>
                      <Layout>
                        <Feedback />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/help-centre" element={
                    <ProtectedRoute>
                      <Layout>
                        <HelpCentrePage />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/network" element={
                    <ProtectedRoute>
                      <Layout>
                        <Network />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <Layout>
                        <Settings />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/devpulse" element={
                    <ProtectedRoute>
                      <Layout>
                        <DevPulse />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/devpulse/:userId" element={
                    <ProtectedRoute>
                      <Layout>
                        <DevPulse />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/leaderboard" element={
                    <ProtectedRoute>
                      <Layout>
                        <Leaderboard />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/notifications" element={
                    <ProtectedRoute>
                      <Layout>
                        <Notifications />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/mentorship" element={
                    <ProtectedRoute>
                      <Layout>
                        <Mentorship />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/calendar" element={
                    <ProtectedRoute>
                      <Layout>
                        <Calendar />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  
                  <Route path="/jobs" element={
                    <ProtectedRoute>
                      <Layout>
                        <Jobs />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/events" element={
                    <ProtectedRoute>
                      <Layout>
                        <Events />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/forum" element={
                    <ProtectedRoute>
                      <Layout>
                        <Forum />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/projects" element={
                    <ProtectedRoute>
                      <Layout>
                        <ProjectShowcase />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/tech-hub" element={
                    <ProtectedRoute>
                      <Layout>
                        <TechHub />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/career-board" element={
                    <ProtectedRoute>
                      <Layout>
                        <CareerBoard />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/project-collaboration" element={
                    <ProtectedRoute>
                      <Layout>
                        <ProjectCollaboration />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/resume" element={
                    <ProtectedRoute>
                      <Layout>
                        <ResumeAnalyzer />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/businesses" element={
                    <ProtectedRoute>
                      <Layout>
                        <BusinessDirectory />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/activity" element={
                    <ProtectedRoute>
                      <Layout>
                        <ActivityHub />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/chat" element={
                    <ProtectedRoute>
                      <Layout>
                        <Chat />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/admin" element={
                    <ProtectedRoute adminOnly>
                      <Layout>
                        <Admin />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/admin-dashboard" element={
                    <ProtectedRoute adminOnly>
                      <Layout>
                        <AdminDashboard />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  {/* Catch all route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
                </ErrorBoundary>
                
                {/* Toast Notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: '#22c55e',
                        secondary: '#fff',
                      },
                    },
                    error: {
                      duration: 5000,
                      iconTheme: {
                        primary: '#ef4444',
                        secondary: '#fff',
                      },
                    },
                  }}
                />
                  </div>
                </CallProvider>
              </Router>

            </NotificationProvider>
          </SocketProvider>
          </ProfileProvider>
      </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
